/**
 * ClienteComboBox — selector de cliente buscable para formularios.
 *
 * v8.6.23 · Fase 52 — antes era un <select> nativo sin buscador: con ~90
 * clientes activos, encontrar uno significaba scrollear la lista entera.
 * Filtra por nombre O id comercial (ej. "CLI-013"). Mismo patrón cmdk +
 * Radix Popover que ya usa components/ClientSelector.jsx (sidebar), pero
 * controlado por value/onChange en vez de atado a ClientContext — este es
 * para un campo de formulario cualquiera, no el cliente "activo" global.
 */
import { useState, useEffect } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Command } from "cmdk";
import { Search, Check, ChevronDown, X } from "../lib/icons.jsx";
import { cn } from "../lib/utils.js";

export default function ClienteComboBox({ clientes, value, onChange, placeholder = "Elegir cliente…", disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  useEffect(() => { if (!open) setQuery(""); }, [open]);

  const seleccionado = (clientes || []).find(c => c.id === value);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button type="button" disabled={disabled}
          className="w-full flex items-center justify-between gap-2 px-3 py-2 border rounded text-sm bg-card hover:border-[#A6ABB6] disabled:opacity-50 disabled:cursor-not-allowed">
          <span className={cn("truncate text-left", !seleccionado && "text-muted-foreground")}>
            {seleccionado ? seleccionado.nombre : placeholder}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0"/>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="start" sideOffset={4}
          className="z-50 w-[var(--radix-popover-trigger-width)] rounded-lg border border-border bg-card shadow-xl overflow-hidden">
          <Command shouldFilter loop>
            <div className="flex items-center gap-2 px-3 border-b border-border">
              <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0"/>
              <Command.Input
                value={query}
                onValueChange={setQuery}
                placeholder="Buscar por nombre o ID…"
                className="flex h-9 w-full bg-transparent text-sm outline-none"
              />
              {query && (
                <button type="button" onClick={() => setQuery("")} className="text-muted-foreground hover:text-muted-foreground">
                  <X className="h-3 w-3"/>
                </button>
              )}
            </div>
            <Command.List className="max-h-64 overflow-y-auto p-1">
              <Command.Empty className="py-4 text-center text-xs text-muted-foreground">Sin resultados</Command.Empty>
              {(clientes || []).map(c => (
                <Command.Item key={c.id} value={`${c.nombre} ${c.id}`}
                  onSelect={() => { onChange(c.id); setOpen(false); }}
                  className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm text-foreground data-[selected=true]:bg-[#E0F7FB] outline-none">
                  <span className="flex-1 truncate">{c.nombre}</span>
                  <span className="text-[12px] text-muted-foreground shrink-0">{c.id}</span>
                  {value === c.id && <Check className="h-3.5 w-3.5 text-[#0E7490] shrink-0"/>}
                </Command.Item>
              ))}
            </Command.List>
          </Command>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
